import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Table, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import flowApi, { type MyFlowTask } from '../../api/flowApi';
import { useAuthStore } from '../../store/authStore';
import { canUseDermato, canUseHepato } from '../../utils/flowAccess';
import { HEPATO_STEP_KEYS } from '../hepato/wizard/HepatoSteps';
import { PHASE_COLORS, PHASE_LABELS } from '../../utils/flowLabels';
import { DERMATO_STEP_KEYS } from '../dermato/wizard/dermatoStepKeys';

const { Title, Text } = Typography;

type TaskRow = MyFlowTask & { kind: 'dermato' | 'hepato' };

/** Lista de lucru: sarcinile deschise pentru rolul curent (hand-off-uri între roluri). */
const TasksPage = () => {
  const navigate = useNavigate();
  const roleName = useAuthStore((state) => state.user?.role?.name);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dermato, hepato] = await Promise.all([
        canUseDermato(roleName) ? flowApi.myTasks('dermato') : Promise.resolve([]),
        canUseHepato(roleName) ? flowApi.myTasks('hepato') : Promise.resolve([]),
      ]);
      setTasks([
        ...dermato.map((t) => ({ ...t, kind: 'dermato' as const })),
        ...hepato.map((t) => ({ ...t, kind: 'hepato' as const })),
      ]);
    } finally {
      setLoading(false);
    }
  }, [roleName]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (task: TaskRow) => {
    const keys = task.kind === 'dermato' ? DERMATO_STEP_KEYS : HEPATO_STEP_KEYS;
    const index = keys.findIndex((k) => k.includes(task.stepKey));
    navigate(`/patients/${task.patientId}/${task.kind}-wizard/${(index >= 0 ? index : 0) + 1}`, {
      state: { editFormId: task.formId, lesionId: task.scopeId || undefined },
    });
  };

  return (
    <Card>
      <Title level={4} style={{ marginTop: 0 }}>Sarcinile mele</Title>
      <Text type="secondary">
        Pași care așteaptă o acțiune din partea rolului tău (trimiși de alți membri ai echipei).
      </Text>
      <Table<TaskRow>
        style={{ marginTop: 16 }}
        rowKey="id"
        loading={loading}
        dataSource={tasks}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: 'Nu ai sarcini deschise.' }}
        columns={[
          { title: 'Sarcină', dataIndex: 'label' },
          {
            title: 'Pacient',
            dataIndex: 'patientId',
            render: (patientId: string) => `HSK-${patientId.slice(0, 6).toUpperCase()}`,
          },
          { title: 'Program', dataIndex: 'kind', render: (k: string) => (k === 'dermato' ? 'Dermato' : 'Hepato') },
          {
            title: 'Fază',
            dataIndex: 'phase',
            render: (phase: MyFlowTask['phase']) => <Tag color={PHASE_COLORS[phase]}>{PHASE_LABELS[phase]}</Tag>,
          },
          {
            title: 'Creată',
            dataIndex: 'createdAt',
            render: (v: string) => new Date(v).toLocaleString('ro-RO'),
          },
          {
            title: '',
            render: (_: unknown, task) => (
              <Button type="primary" size="small" onClick={() => open(task)} data-testid="task-open">
                Deschide
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default TasksPage;
